import { MouseEvent, ReactElement, useMemo } from 'react'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Button, Stack, Typography } from '@mui/material'
import {
  getGuidToHiddenFromTypes,
  getGuidToMGroup,
  getImportedTypes,
  getNotImportedTypes,
  upsertElementTypeAssignment,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import {
  getSelectableGuidsForIfcType,
  mergeSelectedIfcElementGuids,
} from '@editor/components/Controls/IFCUploadWidget/utils/selectionUtils'
import { IfcPropertiesView } from '@editor/components/IFCElementForm/components'
import IFCTypeFilter from '@editor/components/IFCTypeFilter'
import { CorneredSection } from '@ui2/structure'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useSceneStore } from '@editorStores'
import { useIsUpdatingIfcModelMetadata } from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import { getAlignmentMatrix, getGuidToElementGeometry } from '../../utils/glbElementGeometry'
import IFCMetadataPendingNotice from '../IFCMetadataPendingNotice'
import IFCSelectionInfo from '../IFCSelectionInfo'
import IFCTypeAssignmentSelect from '../IFCTypeAssignmentSelect'
import { computeAutoTypeAssignments } from './autoTypeAssignment'

/**
 * Type-assignment mode for the IFC Import page: select IFC elements (click or marquee) and assign
 * them a type by picking it from the dropdown (assignment happens on change, no confirmation). The
 * dropdown is only shown while elements are selected. Shows the shared IFC type/colour filter.
 * "Automatische Typenzuweisung" classifies every element currently visible in the scene purely
 * from its GLB geometry (see `autoTypeAssignment.ts`) — the user hides everything except walls and
 * roof slabs first.
 */
const IFCTypeAssignmentPanel = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const isUpdating = useIsUpdatingIfcModelMetadata()
  const {
    selectedIfcElementGuids,
    setSelectedIfcElementGuids,
    activeGlbFile,
    metadata,
    updateIfcModelMetadata,
  } = useIFCElementSelection()

  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const setGlbTypeVisibility = useControlStore(state => state.setGlbTypeVisibility)
  const applyIfcAlignmentConfig = useControlStore(state => state.applyIfcAlignmentConfig)
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)

  const loadedGltfModels = useSceneStore(state => state.loadedGltfModels)
  const gltfModel = activeGlbFile ? loadedGltfModels[activeGlbFile.guid] : null

  const importedTypes = useMemo(() => getImportedTypes(metadata), [metadata])
  const notImportedTypes = useMemo(() => getNotImportedTypes(metadata), [metadata])
  const guidToMGroup = useMemo(() => getGuidToMGroup(metadata), [metadata])
  const filteredElements = metadata?.filteredElements ?? []

  const handleSelectType = (type: string, event: MouseEvent<HTMLButtonElement>) => {
    const matchingGuids = getSelectableGuidsForIfcType(type, guidToMGroup, filteredElements)
    if (matchingGuids.length === 0) return
    setSelectedIfcElementGuids(
      eventMatchesPointerModifier(event, 'multiSelect')
        ? mergeSelectedIfcElementGuids(selectedIfcElementGuids, matchingGuids)
        : matchingGuids,
    )
  }

  // Classify every element visible in the scene purely from its geometry: vertical elements
  // become inner/outer walls (exterior detection via 2D ray casting), everything else becomes a
  // roof slab. The user hides all other elements first; filtered and hidden elements don't count.
  const handleAutoAssign = () => {
    if (!gltfModel) return
    const filteredGuids = new Set<string>(filteredElements.flat())
    const hiddenFromTypes = getGuidToHiddenFromTypes(guidToMGroup, glbTypeVisibility)
    const isVisible = (guid: string) =>
      !filteredGuids.has(guid) && !hiddenElementIds.has(guid) && !hiddenFromTypes[guid]

    // Aggregated elements (e.g. multi-layer walls) carry their meshes on their group children;
    // classify the parent and attribute the visible children's geometry to it.
    const groupData: Record<string, string[]> = metadata?.group_definition?.data ?? {}
    const childToParent: Record<string, string> = {}
    Object.entries(groupData).forEach(([parent, children]) => {
      children.forEach(child => {
        if (child !== parent) childToParent[child] = parent
      })
    })

    const guidToOwner: Record<string, string> = {}
    Object.keys(guidToMGroup).forEach(guid => {
      if (guid in childToParent || guidToMGroup[guid] === 'openings' || !isVisible(guid)) return
      guidToOwner[guid] = guid
    })
    Object.entries(childToParent).forEach(([child, parent]) => {
      if (guidToOwner[parent] && isVisible(child)) guidToOwner[child] = parent
    })

    const alignmentMatrix = getAlignmentMatrix(
      activeGlbFile?.additional_data?.alignment_config,
      applyIfcAlignmentConfig,
    )
    const guidToGeometry = getGuidToElementGeometry(gltfModel, guidToOwner, alignmentMatrix)
    const assignments = computeAutoTypeAssignments({
      guidToType: guidToMGroup,
      guidToGeometry,
    })
    if (assignments.length === 0) {
      enqueueSnackbar(
        Object.keys(guidToGeometry).length === 0
          ? 'Keine sichtbaren Elemente gefunden. Blenden Sie die zuzuweisenden Wände und Dachflächen ein. Wenn das nicht hilft bitte manuell Zuweisen'
          : 'Keine Typen geändert: Alle sichtbaren Elemente haben bereits den erkannten Typ.',
        { variant: 'info' },
      )
      return
    }

    updateIfcModelMetadata(current => {
      if (!current) return current
      return {
        ...current,
        elementTypeAssignments: assignments.reduce(
          (acc, assignment) => upsertElementTypeAssignment(acc, assignment),
          current.elementTypeAssignments ?? [],
        ),
      }
    })
  }

  const hasTypeAssignments = (metadata?.elementTypeAssignments ?? []).flat().length > 0

  // Delete all manual/automatic type assignments; elements fall back to their IFC-derived type.
  const handleResetAssignments = () => {
    updateIfcModelMetadata(current => {
      if (!current) return current
      return { ...current, elementTypeAssignments: [] }
    })
  }

  if (!metadata)
    return (
      <CorneredSection>
        <IFCMetadataPendingNotice />
      </CorneredSection>
    )

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Elemente per Klick oder Rahmenauswahl markieren und einem Typ zuweisen.
          </Typography>

          <IFCSelectionInfo showWhenEmpty />

          <IFCTypeAssignmentSelect
            guids={selectedIfcElementGuids}
            metadata={metadata}
            disabled={!activeGlbFile}
            updateIfcModelMetadata={updateIfcModelMetadata}
            onAssigned={() => setSelectedIfcElementGuids([])}
          />

          <Typography variant="caption" color="text.secondary">
            Die automatische Zuweisung berücksichtigt nur sichtbare Elemente: Blenden Sie vorher
            alle Elemente außer Wänden und Dachflächen aus. Senkrechte Elemente werden anhand der
            Geometrie zu Innen- bzw. Außenwänden, alle übrigen zu Dachflächen.
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={handleAutoAssign}
            disabled={!activeGlbFile || !gltfModel || isUpdating}
            data-cy="btn-ifc-auto-type-assignment"
          >
            Automatische Typenzuweisung
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleResetAssignments}
            disabled={!activeGlbFile || !hasTypeAssignments || isUpdating}
            data-cy="btn-ifc-reset-type-assignments"
          >
            Zurücksetzen
          </Button>
        </Stack>
      </CorneredSection>

      <IFCTypeFilter
        importedTypes={importedTypes}
        notImportedTypes={notImportedTypes}
        glbTypeVisibility={glbTypeVisibility}
        onToggleTypeVisibility={setGlbTypeVisibility}
        onSelectType={handleSelectType}
      />

      {selectedIfcElementGuids.length > 0 && (
        <CorneredSection title="Eigenschaften" collapsible defaultOpen>
          <IfcPropertiesView
            guids={selectedIfcElementGuids}
            metadata={metadata}
            onSelectGuids={setSelectedIfcElementGuids}
          />
        </CorneredSection>
      )}
    </Stack>
  )
}

export default IFCTypeAssignmentPanel
